import { useState } from "react";
import { motion } from "framer-motion";
import { Reveal } from "@/components/Reveal";
import { HOME } from "@/constants/testIds";
import { GENRES } from "@/lib/constants";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// released tracks: only the three accepted streaming platforms
const RELEASED_HOSTS = ["open.spotify.com", "music.apple.com", "soundcloud.com"];

// pre-save / private streaming hosts for unreleased material — never
// file-download services (drive/dropbox/wetransfer are deliberately excluded).
const PRESAVE_HOSTS = [
    // private streaming — a private soundcloud secret link still lives on the
    // public soundcloud host, so accepting soundcloud.com covers it.
    "open.spotify.com",
    "music.apple.com",
    "soundcloud.com",
    "on.soundcloud.com",
    // pre-save / smart-link platforms
    "feature.fm",
    "ffm.to",
    "hypeddit.com",
    "linkfire.com",
    "lnk.to",
    "orcd.co",
    "distrokid.com",
    "songwhip.com",
    "show.co",
    "presave.io",
    "laylo.com",
    "mtr.bio",
    "empire.link",
    "symphonic.link",
    "unitedmasters.com",
    "toneden.io",
    "artist.link",
];

const RELEASED_ERR = "We only accept Spotify, Apple Music and SoundCloud links.";
const PRESAVE_ERR = "Use a pre-save link (feature.fm, ffm.to, hypeddit, linkfire, orcd, etc.) or a private Spotify, Apple Music or SoundCloud link. No file-hosting or download links.";

const EMPTY = {
    submission_type: "released",
    first_name: "",
    artist_name: "",
    track_name: "",
    track_url: "",
    release_date: "",
    private_url: "",
    genre: "",
    email: "",
    note: "",
    website: "",
};

const hostOf = (raw) => {
    try {
        const url = new URL(raw.trim());
        if (url.protocol !== "https:" && url.protocol !== "http:") return null;
        return { host: url.hostname.replace(/^www\./, "").toLowerCase(), url };
    } catch {
        return null;
    }
};

const matchHost = (host, list) =>
    list.some((h) => host === h || host.endsWith(`.${h}`));

const isoToday = () => new Date().toISOString().slice(0, 10);

const validate = (values) => {
    const errors = {};
    const isUnreleased = values.submission_type === "unreleased";

    if (!values.first_name.trim()) errors.first_name = "name is required";
    // artist_name is optional
    if (!values.track_name.trim()) errors.track_name = "track name is required";

    if (!isUnreleased) {
        if (!values.track_url.trim()) {
            errors.track_url = "a link to the track is required";
        } else {
            const parsed = hostOf(values.track_url);
            if (!parsed) {
                errors.track_url = "that does not look like a valid link";
            } else if (!matchHost(parsed.host, RELEASED_HOSTS)) {
                errors.track_url = RELEASED_ERR;
            }
        }
    } else {
        if (!values.release_date) {
            errors.release_date = "release date is required";
        } else if (values.release_date <= isoToday()) {
            errors.release_date = "release date must be in the future";
        }
        if (!values.private_url.trim()) {
            errors.private_url = "a pre-save or private streaming link is required";
        } else {
            const parsed = hostOf(values.private_url);
            if (!parsed) {
                errors.private_url = "that does not look like a valid link";
            } else if (!matchHost(parsed.host, PRESAVE_HOSTS)) {
                errors.private_url = PRESAVE_ERR;
            }
        }
    }

    if (!values.email.trim()) {
        errors.email = "your email is required";
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(values.email.trim())) {
        errors.email = "that does not look like a valid email";
    }
    if (values.note.length > 600) errors.note = "keep the note under 600 characters";
    return errors;
};

const labelCls = "eyebrow block mb-3";
const inputCls =
    "w-full bg-transparent border-0 border-b border-[var(--ink)] px-0 py-3 text-base text-[var(--ink)] placeholder:text-[var(--ink)] placeholder:opacity-40 focus:outline-none focus:border-[var(--terracotta-ink)] transition-colors duration-200";

const Field = ({ name, label, error, children }) => (
    <div>
        <label className={labelCls} htmlFor={name}>
            {label}
        </label>
        {children}
        {error && (
            <p data-testid={HOME.fieldErrorFor(name)} className="mt-2 text-sm text-[var(--terracotta-ink)]" role="alert">
                {error}
            </p>
        )}
    </div>
);

export const SubmitTrack = () => {
    const [values, setValues] = useState(EMPTY);
    const [errors, setErrors] = useState({});
    const [status, setStatus] = useState("idle");
    const [serverError, setServerError] = useState("");

    const isUnreleased = values.submission_type === "unreleased";

    const set = (name) => (e) => {
        setValues((v) => ({ ...v, [name]: e.target.value }));
        setErrors((prev) => (prev[name] ? { ...prev, [name]: undefined } : prev));
    };

    const setSubmissionType = (type) => {
        setValues((v) => ({ ...v, submission_type: type }));
        // clear errors that no longer apply to the new mode
        setErrors((prev) => {
            const next = { ...prev };
            delete next.track_url;
            delete next.release_date;
            delete next.private_url;
            return next;
        });
    };

    const onSubmit = async (e) => {
        e.preventDefault();
        const found = validate(values);
        setErrors(found);
        if (Object.keys(found).length) return;

        setStatus("submitting");
        setServerError("");
        try {
            const payload = {
                submission_type: values.submission_type,
                first_name: values.first_name.trim(),
                artist_name: values.artist_name.trim() || null,
                track_name: values.track_name.trim(),
                genre: values.genre || null,
                email: values.email.trim(),
                note: values.note.trim() || null,
                website: values.website,
            };
            if (isUnreleased) {
                payload.release_date = values.release_date;
                payload.private_url = values.private_url.trim();
            } else {
                payload.track_url = values.track_url.trim();
            }
            const res = await fetch(`${API}/submissions`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify(payload),
            });
            if (!res.ok) {
                const body = await res.json().catch(() => ({}));
                const detail = body.detail;
                throw new Error(
                    typeof detail === "string" ? detail : "something went wrong. try again in a moment.",
                );
            }
            setValues(EMPTY);
            setStatus("success");
        } catch (err) {
            setServerError(err.message || "something went wrong. try again in a moment.");
            setStatus("error");
        }
    };

    return (
        <section id="submit" data-testid={HOME.submit} aria-label="submit a track to deep end grooves" className="border-b border-[var(--rule)] bg-[var(--cream-deep)]">
            <div className="mx-auto max-w-[1240px] px-6 sm:px-10 lg:px-16 py-20 sm:py-24 lg:py-28">
                <div className="grid gap-16 lg:grid-cols-[minmax(0,1fr)_minmax(0,1.25fr)]">
                    <Reveal>
                        <p className="eyebrow text-[var(--terracotta-ink)]">03 — submit a track</p>
                        <h2 className="font-display mt-6 text-4xl sm:text-5xl max-w-[16ch]">
                            house music submissions
                        </h2>
                        <div className="prose-body mt-8 space-y-5 text-base max-w-[42ch]">
                            <p>
                                the scope is deep house, deep tech and minimal deep tech, dub techno and dub lounge.
                                one track at a time, please, with a link that plays without a login. spotify, apple
                                music or soundcloud.
                            </p>
                            <p>
                                unreleased material is welcome too. pitch a pre-release with the release date and a
                                pre-save link or a private soundcloud, spotify or apple music link — send it with
                                enough lead time to be useful.
                            </p>
                            <p>
                                everything sent through this form gets listened to. it goes straight to the person who
                                picks the music, not to an inbox nobody opens.
                            </p>
                            <p>
                                only successful submissions get a reply. if you do not hear back, it was heard and it
                                was not a fit — send the next one.
                            </p>
                        </div>
                    </Reveal>

                    <Reveal delay={0.05}>
                        {status === "success" ? (
                            <motion.div
                                data-testid={HOME.submitSuccess}
                                initial={{ opacity: 0, y: 16 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ duration: 0.55, ease: [0.22, 0.61, 0.36, 1] }}
                                className="border border-[var(--ink)] p-10"
                                style={{
                                    background: "rgba(239, 231, 213, 0.78)",
                                    backdropFilter: "blur(10px)",
                                    WebkitBackdropFilter: "blur(10px)",
                                }}
                                role="status"
                            >
                                <p className="eyebrow text-[var(--terracotta-ink)]">track received</p>
                                <h3 className="font-display mt-5 text-3xl">it&apos;s in the queue</h3>
                                <p className="prose-body mt-5 text-base max-w-[36ch]">
                                    the track has been sent through. it will be listened to. you will only hear back if
                                    it goes into the playlist.
                                </p>
                                <button
                                    type="button"
                                    onClick={() => setStatus("idle")}
                                    className="eyebrow mt-8 border-b border-[var(--ink)] pb-1 hover:text-[var(--terracotta-ink)] transition-colors duration-200"
                                    data-testid="submit-another"
                                >
                                    submit another track
                                </button>
                            </motion.div>
                        ) : (
                            <form
                                data-testid={HOME.submitForm}
                                onSubmit={onSubmit}
                                noValidate
                                className="border border-[var(--ink)] p-8 sm:p-10 space-y-8"
                                style={{
                                    background: "rgba(239, 231, 213, 0.78)",
                                    backdropFilter: "blur(10px)",
                                    WebkitBackdropFilter: "blur(10px)",
                                }}
                            >
                                <fieldset className="space-y-4">
                                    <legend className={labelCls}>submission type</legend>
                                    <p className="prose-body -mt-2 mb-4 text-sm opacity-80 max-w-[52ch]">
                                        released tracks are pitched with a public link. unreleased tracks are welcome
                                        as pre-release pitches — send them with enough lead time to be useful.
                                    </p>
                                    <div className="grid gap-3 sm:grid-cols-2">
                                        <label
                                            className={`cursor-pointer border px-5 py-4 text-sm uppercase tracking-[0.08em] transition-colors duration-200 ${
                                                !isUnreleased
                                                    ? "border-[var(--ink)] bg-[var(--ink)] text-[var(--cream)]"
                                                    : "border-[var(--ink)] text-[var(--ink)] hover:text-[var(--terracotta-ink)]"
                                            }`}
                                        >
                                            <input
                                                type="radio"
                                                name="submission_type"
                                                value="released"
                                                data-testid={HOME.submissionTypeReleased}
                                                checked={!isUnreleased}
                                                onChange={() => setSubmissionType("released")}
                                                className="sr-only"
                                            />
                                            released
                                        </label>
                                        <label
                                            className={`cursor-pointer border px-5 py-4 text-sm uppercase tracking-[0.08em] transition-colors duration-200 ${
                                                isUnreleased
                                                    ? "border-[var(--ink)] bg-[var(--ink)] text-[var(--cream)]"
                                                    : "border-[var(--ink)] text-[var(--ink)] hover:text-[var(--terracotta-ink)]"
                                            }`}
                                        >
                                            <input
                                                type="radio"
                                                name="submission_type"
                                                value="unreleased"
                                                data-testid={HOME.submissionTypeUnreleased}
                                                checked={isUnreleased}
                                                onChange={() => setSubmissionType("unreleased")}
                                                className="sr-only"
                                            />
                                            unreleased / pre-release
                                        </label>
                                    </div>
                                </fieldset>

                                <Field error={errors.first_name} name="first_name" label="name">
                                    <input
                                        id="first_name"
                                        data-testid="field-first-name"
                                        className={inputCls}
                                        value={values.first_name}
                                        onChange={set("first_name")}
                                        placeholder="your name"
                                        autoComplete="given-name"
                                        required
                                    />
                                </Field>

                                <Field error={errors.artist_name} name="artist_name" label="artist / project name (optional)">
                                    <input
                                        id="artist_name"
                                        data-testid={HOME.fieldArtist}
                                        className={inputCls}
                                        value={values.artist_name}
                                        onChange={set("artist_name")}
                                        placeholder="who made it"
                                        autoComplete="off"
                                    />
                                </Field>

                                <Field error={errors.track_name} name="track_name" label="track name">
                                    <input
                                        id="track_name"
                                        data-testid={HOME.fieldTrack}
                                        className={inputCls}
                                        value={values.track_name}
                                        onChange={set("track_name")}
                                        placeholder="title, and remix if relevant"
                                        autoComplete="off"
                                        required
                                    />
                                </Field>

                                {!isUnreleased ? (
                                    <Field
                                        error={errors.track_url}
                                        name="track_url"
                                        label="link to the track (Spotify, Apple Music or SoundCloud)"
                                    >
                                        <input
                                            id="track_url"
                                            type="url"
                                            inputMode="url"
                                            data-testid={HOME.fieldUrl}
                                            className={inputCls}
                                            value={values.track_url}
                                            onChange={set("track_url")}
                                            placeholder="a Spotify track URL"
                                            autoComplete="off"
                                            required
                                        />
                                        <p className="mt-3 text-xs opacity-80">
                                            spotify, apple music or soundcloud
                                        </p>
                                    </Field>
                                ) : (
                                    <>
                                        <Field
                                            error={errors.release_date}
                                            name="release_date"
                                            label="release date"
                                        >
                                            <input
                                                id="release_date"
                                                type="date"
                                                min={isoToday()}
                                                data-testid={HOME.fieldReleaseDate}
                                                className={inputCls}
                                                value={values.release_date}
                                                onChange={set("release_date")}
                                                required
                                            />
                                        </Field>
                                        <Field
                                            error={errors.private_url}
                                            name="private_url"
                                            label="private or pre-save link"
                                        >
                                            <input
                                                id="private_url"
                                                type="url"
                                                inputMode="url"
                                                data-testid={HOME.fieldPrivateUrl}
                                                className={inputCls}
                                                value={values.private_url}
                                                onChange={set("private_url")}
                                                placeholder="a pre-save or private streaming URL"
                                                autoComplete="off"
                                                required
                                            />
                                            <p className="mt-3 text-xs opacity-80">
                                                pre-save link (feature.fm, ffm.to, hypeddit, linkfire, orcd, etc.) or a
                                                private spotify, apple music or soundcloud link — no file-hosting or
                                                download links
                                            </p>
                                        </Field>
                                    </>
                                )}

                                <Field error={errors.genre} name="genre" label="genre (optional)">
                                    <select
                                        id="genre"
                                        data-testid={HOME.fieldGenre}
                                        className={`${inputCls} appearance-none cursor-pointer`}
                                        value={values.genre}
                                        onChange={set("genre")}
                                    >
                                        <option value="">not sure / not listed</option>
                                        {GENRES.map((g) => (
                                            <option key={g.value} value={g.value}>
                                                {g.label}
                                            </option>
                                        ))}
                                    </select>
                                </Field>

                                <Field error={errors.email} name="email" label="your email">
                                    <input
                                        id="email"
                                        type="email"
                                        data-testid={HOME.fieldEmail}
                                        className={inputCls}
                                        value={values.email}
                                        onChange={set("email")}
                                        placeholder="where a reply would go"
                                        autoComplete="email"
                                        required
                                    />
                                </Field>

                                <Field error={errors.note} name="note" label="note (optional)">
                                    <textarea
                                        id="note"
                                        rows={3}
                                        data-testid={HOME.fieldNote}
                                        className={`${inputCls} resize-none`}
                                        value={values.note}
                                        onChange={set("note")}
                                        placeholder="label, catalogue number, anything short"
                                    />
                                </Field>

                                <div aria-hidden="true" className="absolute left-[-9999px] w-px h-px overflow-hidden">
                                    <label htmlFor="website">website</label>
                                    <input
                                        id="website"
                                        name="website"
                                        tabIndex={-1}
                                        data-testid={HOME.fieldHoneypot}
                                        value={values.website}
                                        onChange={set("website")}
                                        autoComplete="off"
                                    />
                                </div>

                                {status === "error" && (
                                    <p
                                        data-testid={HOME.submitError}
                                        role="alert"
                                        className="border-l-2 border-[var(--terracotta-ink)] pl-4 text-sm text-[var(--terracotta-ink)]"
                                    >
                                        {serverError}
                                    </p>
                                )}

                                <button
                                    type="submit"
                                    data-testid={HOME.submitButton}
                                    disabled={status === "submitting"}
                                    className="w-full border border-[var(--ink)] bg-[var(--ink)] px-8 py-4 text-base font-bold uppercase tracking-[0.08em] text-[var(--cream)] hover:opacity-90 active:opacity-80 disabled:opacity-50 transition-opacity duration-200"
                                >
                                    {status === "submitting" ? "sending" : "send the track"}
                                </button>
                            </form>
                        )}
                    </Reveal>
                </div>
            </div>
        </section>
    );
};
